import React, { useState, useContext, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import Avatar from 'react-avatar';
import Button from '../../../Button';
import Link from '../../../Link';
import ProductSwitcher from '../../../ProductSwitcher';
import { AuthContext } from '../../../../contexts/AuthContext';
import { Theme } from '../../../../interfaces/common';
import { Modules, AuthRoutes } from '../../../../enums';
import { API_URL } from '../../../../utils/apiConstant';

const toAbsolute = (fileName: string | null | undefined): string | undefined => {
  if (!fileName) return undefined;
  return fileName.startsWith('http') ? fileName : `${API_URL}/uploads/avatars/${fileName}`;
};

interface TopbarProps {
  collapsed: boolean;
  onCollapse: (_: boolean) => void;
}

const Topbar: React.FC<TopbarProps> = ({ collapsed, onCollapse }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loggedInUser, theme, setTheme, logout, setLoading } = useContext(AuthContext);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [profileClicked, setProfileClicked] = useState(false);
  const profileRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!profileClicked && !showShortcuts) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileClicked(false);
        setShowShortcuts(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileClicked, showShortcuts]);

  const handleLogoutClick = async () => {
    await logout();
    navigate(Modules.auth);
  };

  const handleAccountNav = (tab: string) => {
    setProfileClicked(false);
    navigate(`${Modules.my}${AuthRoutes.account}?tab=${tab}`);
  };

  const handleThemeClick = (theme: Theme) => {
    setLoading('true');
    setTheme(theme);
  };

  return (
    <nav
      className="layout-navbar container-fluid navbar navbar-expand-xl navbar-detached align-items-center bg-navbar-theme m-0 px-6 rounded-0"
      id="layout-navbar"
    >
      <div className="navbar-nav-right d-flex align-items-center" id="navbar-collapse">
        <ul className="navbar-nav flex-row align-items-center ms-auto">
          <ProductSwitcher />
          <li className="nav-item me-2 d-flex align-items-center">
            <button
              type="button"
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              title={theme === 'dark' ? 'Light Theme' : 'Dark Theme'}
              onClick={(e) => {
                e.stopPropagation();
                handleThemeClick(theme === 'dark' ? 'light' : 'dark');
              }}
              className="light-dark-theme-selector"
            >
              {theme === 'dark' ? (
                <i className="ri-sun-line ri-18px"></i>
              ) : (
                <i className="ri-moon-line ri-18px"></i>
              )}
            </button>
          </li>

        </ul>
      </div>
    </nav>
  );
};

export default Topbar;
